import React, { useMemo, useState, useEffect, useRef } from 'react'
import "./styles.css";
import { Link, useNavigate } from 'react-router-dom';
import { ReactTable } from '../../Components/Molecules';
import { Breadcrumb, OptionList, Title } from '../../Components/Atoms';
import { Button, Spinner, useToast } from '@chakra-ui/react';
import { ChevronRightIcon, DownloadIcon } from '../../Assets/images/svgs';
import { OPERATIONS_HISTORY_HEADERS } from '../../Library/tableHeaders';
import { fetchBulkOperationHistory } from '../../APIs';
import { formatTimestamp, getLocalisedDate, getLocalisedTime } from '../../Library/datetime';
import { BULK_OPERATION_TYPES, BULK_OPR_STATUS_TYPES } from '../../Library/constants';
import { jsonToCSVConvertor } from '../../Library/jsonUtils';
import PATHS from "../../Routes/paths";

const BulkOperationsHistory = () => {
  const Toast = useToast();
  const navigate = useNavigate();
  const [operationsHistory, setOperationsHistory] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [selectedBulkOps, setSelectedBulkOps] = useState(0);
  const selectedSummaries = useRef([]);

  const breadcrumbPaths = [
    { idx: 1, name: "Bulk Operations", isCurrentPage: true },
  ];

  const columns = useMemo(() => {
    const cols = [];
    Object.values(OPERATIONS_HISTORY_HEADERS).forEach((header) => {
      cols.push({
        Header: header.key === "viewSumry" ? "" : header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        disableFilters: !header.showSearch,
        Cell: ({ row, value, column }) => {
          if (column.id === "viewSumry") return <Link to={`${PATHS.BULK_OPERATIONS}${PATHS.OPERATION_SUMMARY}?oprId=${row?.original?.idx}`} className='viewSumry-link'>View Summary <ChevronRightIcon /></Link>
          return value || "-";
        }
      });
    });

    return cols;
  }, []);

  const getBulkOperationsHistory = () => {
    setShowLoading(true);
    fetchBulkOperationHistory((err, data = []) => {
      if (err) {
        console.error(err);
        Toast({ isClosable: true, description: err, position: "bottom", variant: "subtle", duration: 3000 });
      } else {
        if (!data || !Array.isArray(data) || !data.length) return navigate(`${PATHS.BULK_OPERATIONS}${PATHS.NEW_BULK_OPERATION}`);

        data.forEach((opr, index) => {
          opr.srNo = index + 1;
          opr.oprType = BULK_OPERATION_TYPES[opr.oprType];
          opr.vehsCnt = opr?.vehDetails?.length;
          if (opr.ts) {
            opr.date = getLocalisedDate(opr.ts);
            opr.time = formatTimestamp(opr.ts);
          }
        })
        data.sort((a, b) => b.ts - a.ts);
        setOperationsHistory(data);
      }
      setShowLoading(false);
    });
  }

  useEffect(() => {
    getBulkOperationsHistory();
  }, []);

  const downloadData = () => {
    if (!operationsHistory?.length) return;
    var sendToExcel = operationsHistory.map(a => Object.assign({}, a));
    sendToExcel.forEach((operation, idx) => {
      operation["Sr No"] = idx + 1;
      operation["Operation Name"] = operation.oprNm;
      operation["Operation"] = operation.oprType;
      operation["Date"] = operation.date;
      operation["Time"] = operation.time;
      operation["Vehicles"] = operation.count;
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Operation Name, Operation, Date, Time, Vehicles";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Bulk_Operation_History" + Date.now(), false);
  }

  const handleRowSelect = (selectAll, selectedRows) => {
    if (selectAll) selectedSummaries.current = operationsHistory;
    else selectedSummaries.current = selectedRows.map(row => row.original);

    setSelectedBulkOps(selectedSummaries.current.length);
  }

  const downloadDetailedReport = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    let srNo = 1;
    sendToExcel.forEach(operation => {
      operation.vehDetails.forEach(vehicle => {
        vehicle["Sr No"] = srNo;
        vehicle["Operation Name"] = operation.oprNm;
        vehicle["Operation"] = operation.oprType;
        vehicle["Operation Date"] = operation.date;
        vehicle["Operation Time"] = operation.time;
        vehicle["Vehicles"] = vehicle.reg;
        vehicle["Status"] = vehicle.status ? BULK_OPR_STATUS_TYPES[vehicle.status] : "-";
        vehicle["Execution Date"] = vehicle.ts ? getLocalisedDate(vehicle.ts) : "-";
        vehicle["Execution Time"] = vehicle.ts ? getLocalisedTime(vehicle.ts) : "-";

        srNo++;
      });
    });

    sendToExcel = sendToExcel.map(data => data.vehDetails || []).flat();
    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Operation Name, Operation, Operation Date, Operation Time, Vehicles, Status, Execution Date, Execution Time";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });

    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Bulk_Operations_" + Date.now(), false);
  }

  const handleBulkOpsDownload = (option) => {
    downloadDetailedReport(option.downloadAll ? operationsHistory : selectedSummaries.current);
  }

  const ReportsDownloadOptions = [
    { label: <div className="bulk-download-options">All Operations <DownloadIcon /></div>, downloadAll: true },
    { label: <div className="bulk-download-options">{`Selected Operations (${selectedBulkOps})`} <DownloadIcon /></div>, disabled: !selectedBulkOps, tooltipText: "Please select at least one bulk operation" , showTooltip: !selectedBulkOps},
  ];

  return (
    <div className='panel-main bulk-ot-his'>
      <div className='bulk-ot-his-header'>
        <div className='bc-cnt'>
          <Breadcrumb paths={breadcrumbPaths} />
        </div>
        <Button className="add-new-btn" onClick={() => navigate(`${PATHS.BULK_OPERATIONS}${PATHS.NEW_BULK_OPERATION}`)}>
          Perform New Operation
        </Button>
      </div>

      {showLoading ? <div className="bul-opr-loader">
        <Spinner />
      </div> : <div className='history-cnt'>
        <div className="title-cnt">
          <Title>Bulk Operations History</Title>
          <div className="download-btns-container">
            {operationsHistory && operationsHistory.length ? <Button className='his-dwl-btn' onClick={downloadData}><DownloadIcon />Download</Button> : null}
            {operationsHistory && operationsHistory.length ? <OptionList name="With Summary" Icon={DownloadIcon} options={ReportsDownloadOptions} onOptionClick={handleBulkOpsDownload}/> : null}
          </div>
        </div>
        <ReactTable
          data={operationsHistory || []}
          columns={columns}
          showSearch={true}
          pageSize={10}
          showSelection={true}
          showSelectAll={true}
          checkboxLabel={'Select All'}
          onRowSelect={handleRowSelect}
        />
      </div>}
    </div>
  )
}

export default BulkOperationsHistory;